/**
 * Helpers for reading a live-stream anchor document's harvest-written
 * description.
 *
 * Its own module rather than a named export from `LiveStreamPreview` so that
 * file exports only its component (react-refresh/only-export-components), the
 * same reason `persistedDocType` lives in `lib/docType.ts`.
 */

/**
 * Pull the tag list out of the sentence the Eventhouse harvest writes.
 *
 * PRODUCER — `_write_anchor_description` in
 * `backend/core/src/intai/timeseries/eventhouse_harvest.py`. It emits:
 *
 *   "Live Eventhouse stream <db>/<table> — <N> tag(s): A (desc); B (desc).
 *    Values are queried live from Eventhouse at question time."
 *
 * and "… — no tags yet. <tail>" when the inventory is empty.
 *
 * This is prose being re-parsed back into structure, so the coupling is
 * invisible from the producer's side: reword that sentence and this silently
 * returns [], the caller's `length > 0` guard skips the section, and a live
 * stream renders with no tags — indistinguishable from one that genuinely has
 * none. **If you are editing the producer, edit this too.** The durable fix is
 * to carry tags structurally (the rows endpoint's `tag_ids`, or adding them to
 * `EventhouseConnectionSummary`) and treat this as the fallback.
 *
 * Note the count word is singular for exactly one tag ("1 tag:"), which is why
 * the marker is matched with a pattern rather than an `indexOf(' tags: ')`.
 */
export function parseTagInventory(description?: string | null): string[] {
  if (!description) return []
  const marker = / tags?: /.exec(description)
  if (!marker) return []
  const after = description.slice(marker.index + marker[0].length)
  // The producer's tail sentence; kept loose ("Values are queried") so a
  // reworded ending still truncates rather than swallowing it into the last tag.
  const upToSentence = after.split('. Values are queried')[0] ?? after
  return upToSentence
    .split(';')
    .map((t) => t.trim().replace(/\.$/, ''))
    .filter(Boolean)
}
